import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Trash2, Plus } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://kalibr-backend.fly.dev';

export default function APIKeysModal({ isOpen, onClose }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [showNewKeyDialog, setShowNewKeyDialog] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { getToken } = useAuth();

  useEffect(() => {
    if (isOpen) {
      fetchKeys();
      setError('');
    }
  }, [isOpen]);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/settings/api-keys`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setKeys(data);
      } else {
        setError('Failed to load API keys');
      }
    } catch (err) {
      setError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) {
      setError('Please enter a key name');
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/settings/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newKeyName,
          rate_limit_qps: 1000,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setNewKey(data.api_key);
        setShowNewKeyDialog(true);
        setNewKeyName('');
        setError('');
        fetchKeys();
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || 'Failed to create API key');
      }
    } catch (err) {
      setError('Failed to create API key');
    }
  };

  const deleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) {
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/settings/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchKeys();
      } else {
        setError('Failed to delete API key');
      }
    } catch (err) {
      setError('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-zinc-800">
            <div>
              <h2 className="text-xl font-bold text-white">API Keys</h2>
              <p className="mt-1 text-sm text-zinc-500">
                Manage API keys for SDK authentication
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-zinc-500 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
            {error && (
              <div className="mb-4 bg-red-950 border border-red-900 text-red-400 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}

            {/* New Key Created Dialog */}
            {showNewKeyDialog && newKey && (
              <div className="mb-6 bg-green-950 border border-green-900 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-green-400 mb-2">API Key Created!</h3>
                <p className="text-xs text-zinc-400 mb-3">
                  Save this key securely. You won't be able to see it again.
                </p>
                <div className="bg-zinc-900 p-3 rounded font-mono text-sm text-white break-all mb-3 border border-zinc-800">
                  {newKey}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(newKey)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded text-sm transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => {
                      setShowNewKeyDialog(false);
                      setNewKey(null);
                    }}
                    className="px-3 py-1.5 text-zinc-400 hover:text-white text-sm transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Create New Key */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-white mb-3">Create New API Key</h3>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g., Production SDK)"
                  className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-zinc-600"
                  onKeyDown={(e) => e.key === 'Enter' && createKey()}
                />
                <button
                  onClick={createKey}
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded text-sm font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create
                </button>
              </div>
            </div>

            {/* Keys List */}
            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading...</div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="border-b border-zinc-800 text-left text-zinc-400 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 font-medium">Name</th>
                      <th className="px-4 py-3 font-medium">Key Prefix</th>
                      <th className="px-4 py-3 font-medium">Created</th>
                      <th className="px-4 py-3 font-medium">Last Used</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-zinc-500 text-sm">
                          No API keys yet. Create one to get started.
                        </td>
                      </tr>
                    ) : (
                      keys.map((key) => (
                        <tr key={key.id} className="border-b border-zinc-800 last:border-0">
                          <td className="px-4 py-3 text-sm text-white">
                            {key.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-zinc-400 font-mono">
                            {key.key_prefix}...
                          </td>
                          <td className="px-4 py-3 text-sm text-zinc-500">
                            {new Date(key.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-zinc-500">
                            {key.last_used
                              ? new Date(key.last_used).toLocaleDateString()
                              : 'Never'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {key.is_active ? (
                              <span className="px-2 py-1 text-xs bg-green-950 text-green-400 border border-green-900 rounded">
                                Active
                              </span>
                            ) : (
                              <span className="px-2 py-1 text-xs bg-zinc-800 text-zinc-400 border border-zinc-700 rounded">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right">
                            <button
                              onClick={() => deleteKey(key.id)}
                              className="text-red-500 hover:text-red-400 transition-colors p-1"
                              title="Delete key"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Usage Instructions */}
            <div className="mt-6 p-4 bg-zinc-900 border border-zinc-800 rounded-lg">
              <h3 className="text-sm font-semibold text-white mb-2">Quick Start</h3>
              <div className="font-mono text-xs text-zinc-400 space-y-1">
                <p><span className="text-zinc-600"># Install SDK</span></p>
                <p className="text-green-400">pip install kalibr</p>
                <p className="mt-2"><span className="text-zinc-600"># Set environment variables</span></p>
                <p className="text-green-400">export KALIBR_API_KEY=sk_...</p>
                <p className="text-green-400">export KALIBR_COLLECTOR_URL=https://kalibr-backend.fly.dev/api/ingest</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
